import { useState } from 'react';
import { Trash2, Plus, Minus, Save, ShoppingCart, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useShoppingContext } from '@/context/ShoppingContext';
import { getProductById, getStoreById, storeListings } from '@/data/mockData';
import { Link } from 'react-router-dom';
import Seo from '@/components/Seo';

export default function ShoppingListPage() {
  const {
    items, removeItem, updateQuantity, clearList,
    saveCurrentList, storeBaskets, mixedRecommendation, cheapestSingleStore,
  } = useShoppingContext();
  const [saveName, setSaveName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);

  const handleSave = () => {
    if (!saveName.trim()) return;
    saveCurrentList(saveName.trim());
    setSaveName('');
    setShowSaveInput(false);
  };

  if (items.length === 0) {
    return (
      <div className="container py-20 text-center pb-24 md:pb-6">
        <Seo
          title="My shopping list | Grozo"
          description="Build a smart grocery list and see the cheapest single store or optimal mix across Indian quick-commerce stores."
          path="/list"
          noindex
        />
        <div className="animate-slide-up mx-auto max-w-md">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-muted" style={{ animation: 'float 3s ease-in-out infinite' }}>
            <span className="text-4xl">🛒</span>
          </div>
          <h1 className="font-display text-3xl font-bold mb-3">Your list is empty</h1>
          <p className="text-muted-foreground mb-8">Add items from the Compare page to start building your smart shopping list.</p>
          <Button asChild size="lg" className="gap-2 rounded-2xl px-8">
            <Link to="/compare">
              Browse Products
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8 pb-24 md:pb-8">
      <Seo
        title="My shopping list | Grozo"
        description="Build a smart grocery list and see the cheapest single store or optimal mix across Indian quick-commerce stores."
        path="/list"
        noindex
      />
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-accent">
            <ShoppingCart className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold">My Shopping List</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{items.length} items · comparing across 6 stores</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowSaveInput(!showSaveInput)} className="gap-1.5 rounded-xl">
            <Save className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Save</span>
          </Button>
          <Button variant="outline" size="sm" onClick={clearList} className="gap-1.5 text-destructive hover:text-destructive rounded-xl">
            <Trash2 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Clear</span>
          </Button>
        </div>
      </div>

      {showSaveInput && (
        <div className="flex gap-2 mb-6 animate-slide-up">
          <Input value={saveName} onChange={e => setSaveName(e.target.value)} placeholder="List name..." className="max-w-xs rounded-xl" />
          <Button size="sm" onClick={handleSave} className="rounded-xl">Save List</Button>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        {/* Items */}
        <div className="space-y-2.5">
          {items.map((item, i) => {
            const product = getProductById(item.productId);
            const isScraped = !!item.scraped;
            const displayName = isScraped ? item.scraped!.name : product?.name || 'Unknown';
            const displayBrand = isScraped ? (item.scraped!.brand || '') : (product ? `${product.brand} · ${product.size}` : '');
            const displayImage = isScraped ? item.scraped!.image : product?.image;
            
            let bestInfo = '';
            if (isScraped) {
              bestInfo = `₹${item.scraped!.price} at ${item.scraped!.store}`;
            } else {
              const cheapest = storeListings
                .filter(l => l.productId === item.productId && l.availability)
                .sort((a, b) => a.price - b.price)[0];
              const store = cheapest ? getStoreById(cheapest.storeId) : null;
              if (cheapest && store) bestInfo = `Best: ₹${cheapest.price.toFixed(0)} at ${store.name}`;
            }

            return (
              <div
                key={item.productId}
                className={`animate-slide-up stagger-${Math.min(i + 1, 6)} hover-lift flex items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm`}
              >
                {isScraped && displayImage ? (
                  <img src={displayImage} alt={displayName} className="w-10 h-10 object-contain rounded-lg shrink-0" loading="lazy" />
                ) : (
                  <span className="text-3xl shrink-0">{typeof displayImage === 'string' && !displayImage.startsWith('http') ? displayImage : '🛒'}</span>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-card-foreground truncate">{displayName}</p>
                  {displayBrand && <p className="text-xs text-muted-foreground">{displayBrand}</p>}
                  {bestInfo && (
                    <p className="text-xs text-primary mt-0.5 font-medium">{bestInfo}</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <Button variant="outline" size="icon" aria-label="Decrease quantity" className="h-8 w-8 rounded-lg" onClick={() => updateQuantity(item.productId, item.quantity - 1)}>
                    <Minus className="h-3 w-3" />
                  </Button>
                  <span className="w-8 text-center text-sm font-bold">{item.quantity}</span>
                  <Button variant="outline" size="icon" aria-label="Increase quantity" className="h-8 w-8 rounded-lg" onClick={() => updateQuantity(item.productId, item.quantity + 1)}>
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
                <Button variant="ghost" size="icon" aria-label="Remove item" className="h-8 w-8 rounded-lg text-muted-foreground hover:text-destructive" onClick={() => removeItem(item.productId)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>

        {/* Recommendations panel */}
        <div className="space-y-5">
          {cheapestSingleStore && (
            <div className="animate-slide-up stagger-1 rounded-2xl border border-border bg-card p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <ShoppingCart className="h-4 w-4 text-primary" />
                <h2 className="font-display text-base font-semibold">Best Single Store</h2>
              </div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{getStoreById(cheapestSingleStore.storeId)?.logo || '🏪'}</span>
                  <span className="font-semibold">{getStoreById(cheapestSingleStore.storeId)?.name || cheapestSingleStore.storeId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                </div>
                <span className="text-2xl font-bold text-primary">₹{cheapestSingleStore.total.toFixed(0)}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {cheapestSingleStore.availableItems}/{cheapestSingleStore.totalItems} items available
              </p>
              {cheapestSingleStore.savings > 0 && (
                <Badge className="mt-3 bg-primary/10 text-primary border border-primary/20">
                  Save ₹{cheapestSingleStore.savings.toFixed(0)} vs most expensive store
                </Badge>
              )}
            </div>
          )}

          {mixedRecommendation && (
            <div className="animate-slide-up stagger-2 rounded-2xl border-2 border-primary/40 bg-gradient-to-br from-primary/5 to-accent/30 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-4 w-4 text-primary" />
                <h2 className="font-display text-base font-semibold">Optimal Mix</h2>
                <Badge className="bg-primary text-primary-foreground text-[10px] ml-auto">Best Value</Badge>
              </div>
              <p className="text-3xl font-bold text-primary mb-2">₹{mixedRecommendation.total.toFixed(0)}</p>
              {mixedRecommendation.savings > 0 && (
                <p className="text-xs text-primary font-semibold mb-4">
                  Save ₹{mixedRecommendation.savings.toFixed(0)} by shopping at multiple stores
                </p>
              )}
              <div className="space-y-2">
                {mixedRecommendation.assignments.map(a => {
                  const product = getProductById(a.productId);
                  const store = getStoreById(a.storeId);
                  const listItem = items.find(i => i.productId === a.productId);
                  const displayName = listItem?.scraped ? listItem.scraped.name : (product?.name || 'Unknown');
                  const displayEmoji = listItem?.scraped ? '🛒' : (product?.image || '');
                  const storeName = listItem?.scraped ? listItem.scraped.store : (store?.name || a.storeId);
                  return (
                    <div key={a.productId} className="flex items-center justify-between text-xs rounded-lg bg-background/60 px-3 py-2">
                      <span className="text-card-foreground truncate flex-1">{displayEmoji} {displayName} ×{a.quantity}</span>
                      <span className="text-muted-foreground ml-2">{storeName} ₹{(a.price * a.quantity).toFixed(0)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="animate-slide-up stagger-3 rounded-2xl border border-border bg-card p-6 shadow-sm">
            <h2 className="font-display text-base font-semibold mb-4">All Store Totals</h2>
            <div className="space-y-2">
              {storeBaskets.map((basket, i) => {
                const store = getStoreById(basket.storeId);
                return (
                  <div key={basket.storeId} className={`flex items-center justify-between rounded-xl px-4 py-2.5 text-sm transition-colors ${i === 0 ? 'bg-primary/10 dark:bg-primary/15' : 'bg-muted/40'}`}>
                    <span className="flex items-center gap-2">
                      <span>{store?.logo}</span>
                      <span className="font-medium">{store?.name}</span>
                      {i === 0 && <Badge className="bg-primary text-primary-foreground text-[9px]">Cheapest</Badge>}
                    </span>
                    <span className={`font-bold ${i === 0 ? 'text-primary' : 'text-card-foreground'}`}>
                      ₹{basket.total.toFixed(0)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
